import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, LogIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { to: "/", label: "Utama" },
  { to: "/tentang", label: "Tentang" },
  { to: "/kepimpinan", label: "Kepimpinan" },
  { to: "/jurulatih", label: "Jurulatih" },
  { to: "/keahlian", label: "Keahlian" },
  { to: "/direktori", label: "Direktori" },
  { to: "/program", label: "Program" },
  { to: "/berita", label: "Berita" },
  { to: "/hubungi", label: "Hubungi" },
];

export function PublicHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome;

  return (
    <>
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-piks-black/95 backdrop-blur-xl border-b border-white/10 shadow-lg" : "bg-piks-black/30 backdrop-blur-sm"
      }`}
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 2xl:px-8 h-16 flex items-center justify-between gap-3 lg:gap-4 2xl:gap-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 min-w-0" data-testid="header-logo-link">
          <img src="/piks-logo.png?v=7" alt="Bodyguard Sarawak" className="h-10 w-10 sm:h-11 sm:w-11 object-contain shrink-0" />
          <div className="leading-tight min-w-0">
            <p className="font-display text-white text-[12px] sm:text-[14px] font-extrabold tracking-wide truncate">BODYGUARD SARAWAK</p>
            <p className="text-[8px] sm:text-[9px] text-white/50 tracking-[0.1em] sm:tracking-[0.15em] truncate">PERSATUAN PENGAWAL PERIBADI SARAWAK</p>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-3.5 2xl:gap-6 shrink-0">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative py-2 text-[12px] 2xl:text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-white" : "text-white/70 hover:text-white"}`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active-indicator"
                      className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-piks-red"
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                  {isActive && <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-piks-gold" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-1.5 2xl:gap-3 shrink-0">
          <Link to="/semak" data-testid="header-semak-ahli-btn">
            <Button variant="outline" size="sm" className="border-piks-gold/50 text-piks-gold hover:bg-piks-gold hover:text-piks-black bg-transparent rounded-full h-9 px-2.5 2xl:px-4 text-[11px] 2xl:text-xs font-semibold tracking-wide whitespace-nowrap transition-all">
              SEMAK AHLI
            </Button>
          </Link>
          {user ? (
            <Button
              size="sm"
              data-testid="header-dashboard-btn"
              onClick={() => navigate(user.role === "member" ? "/app" : "/admin")}
              className="rounded-full h-9 px-2.5 2xl:px-4 text-[11px] 2xl:text-xs font-semibold tracking-wide whitespace-nowrap bg-white text-piks-black hover:bg-white/90"
            >
              Dashboard
            </Button>
          ) : (
            <Link to="/login" data-testid="header-login-btn">
              <Button variant="ghost" size="sm" className="text-white hover:text-piks-gold hover:bg-white/5 h-9 px-2 2xl:px-3 text-[11px] 2xl:text-xs font-semibold tracking-wide whitespace-nowrap">
                <LogIn className="w-3.5 h-3.5 2xl:mr-1" /> <span className="hidden 2xl:inline">LOG MASUK</span>
              </Button>
            </Link>
          )}
          <Link to="/tuntut-keahlian" data-testid="header-tuntut-ahli-btn">
            <Button size="sm" className="rounded-full h-9 px-3 2xl:px-5 text-[11px] 2xl:text-xs font-bold tracking-wide whitespace-nowrap bg-piks-red hover:bg-piks-red/90 text-white shadow-lg shadow-piks-red/20 group">
              TUNTUT AHLI
            </Button>
          </Link>
        </div>

        <button
          className="xl:hidden text-white p-2 ml-auto"
          onClick={() => setOpen(!open)}
          data-testid="header-mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="xl:hidden fixed top-16 right-0 bottom-0 left-0 z-40 bg-piks-black overflow-y-auto"
            data-testid="mobile-nav-menu"
          >
            <img src="/piks-logo.png?v=7" alt="" className="crest-watermark absolute -right-16 top-10 w-72 object-contain select-none" />
            <div className="diagonal-accent absolute left-0 top-0 h-full opacity-40">
              <span />
              <span />
              <span />
            </div>
            <div className="relative z-10 px-6 py-8 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: 0.06 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-3 font-display text-2xl font-extrabold tracking-tight ${isActive ? "text-piks-gold" : "text-white/90"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="pt-6 flex flex-col gap-3">
                <Link to="/semak" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full border-piks-gold/50 text-piks-gold rounded-full h-11" data-testid="mobile-semak-ahli-btn">
                    <ShieldCheck className="w-4 h-4 mr-1.5" /> SEMAK AHLI
                  </Button>
                </Link>
                {user ? (
                  <Button onClick={() => { setOpen(false); navigate(user.role === "member" ? "/app" : "/admin"); }} className="w-full bg-white text-piks-black rounded-full h-11">
                    Dashboard
                  </Button>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)}>
                    <Button variant="ghost" className="w-full text-white rounded-full h-11 border border-white/15">LOG MASUK</Button>
                  </Link>
                )}
                <Link to="/tuntut-keahlian" onClick={() => setOpen(false)}>
                  <Button className="w-full bg-piks-red text-white rounded-full font-bold h-11" data-testid="mobile-tuntut-ahli-btn">TUNTUT AHLI</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
